/**
 * Cadastro de clientes (`/clientes`). Tabela paginada com busca, filtro de UF e
 * de ativos; criação/edição em modal; exclusão trata o 409 `cliente_em_uso`.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { isValidDocumento, maskDocumento, maskPhone } from '@/lib/format';
import type { CreateClientInput } from '@/lib/types';
import {
  useAtualizarCliente,
  useCriarCliente,
  useEstados,
  useExcluirCliente,
  useClientesLista,
  type ClienteResumo,
} from '@/hooks/useClientes';
import { useToast } from '@/hooks/useToast';
import { PageHeader } from '@/components/PageHeader';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Modal } from '@/components/Modal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import './Clientes.css';

type FiltroAtivo = '' | '1' | '0';
type EstadoModal = { modo: 'novo' } | { modo: 'editar'; cliente: ClienteResumo } | null;

const PER_PAGE = 20;

export function Clientes() {
  const navigate = useNavigate();
  const toast = useToast();

  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const [uf, setUf] = useState('');
  const [ativo, setAtivo] = useState<FiltroAtivo>('');
  const [pagina, setPagina] = useState(1);
  const [modal, setModal] = useState<EstadoModal>(null);
  const [paraExcluir, setParaExcluir] = useState<ClienteResumo | null>(null);
  const [erroExclusao, setErroExclusao] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setBuscaDebounced(busca.trim()), 300);
    return () => window.clearTimeout(t);
  }, [busca]);

  useEffect(() => {
    setPagina(1);
  }, [buscaDebounced, uf, ativo]);

  const estadosQuery = useEstados();
  const listaQuery = useClientesLista({
    q: buscaDebounced || undefined,
    uf: uf || undefined,
    active: ativo || undefined,
    page: pagina,
    perPage: PER_PAGE,
  });

  const atualizar = useAtualizarCliente();
  const excluir = useExcluirCliente();

  const linhas = listaQuery.data?.data ?? [];
  const total = listaQuery.data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(total / PER_PAGE));
  const semFiltros = buscaDebounced === '' && uf === '' && ativo === '';

  function alternarAtivo(cliente: ClienteResumo) {
    atualizar.mutate(
      { id: cliente.id, input: { active: !cliente.active } },
      {
        onSuccess: () => toast.success(cliente.active ? `${cliente.razaoSocial} desativado.` : `${cliente.razaoSocial} ativado.`),
        onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Não foi possível salvar agora.'),
      }
    );
  }

  function confirmarExclusao() {
    if (!paraExcluir) return;
    setErroExclusao(null);
    excluir.mutate(paraExcluir.id, {
      onSuccess: () => {
        toast.success(`${paraExcluir.razaoSocial} excluído.`);
        setParaExcluir(null);
      },
      onError: (err) => setErroExclusao(err instanceof ApiError ? err.message : 'Não foi possível excluir agora.'),
    });
  }

  const colunas: DataTableColumn<ClienteResumo>[] = [
    { key: 'razaoSocial', header: 'Razão social', render: (row) => row.razaoSocial },
    { key: 'cnpj', header: 'CNPJ / CPF', render: (row) => <span className="mono">{row.cnpj}</span> },
    { key: 'contato', header: 'Contato', render: (row) => row.contato || <span className="faint">—</span> },
    { key: 'uf', header: 'UF', render: (row) => row.uf || <span className="faint">—</span> },
    { key: 'propostas', header: 'Propostas', numeric: true, render: (row) => row.proposalsCount },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <Badge variant={row.active ? 'positive' : 'neutral'}>{row.active ? 'Ativo' : 'Inativo'}</Badge>,
    },
    {
      key: 'acoes',
      header: 'Ações',
      className: 'cli-actions-cell',
      render: (row) => (
        <div className="row row-wrap cli-actions">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              setModal({ modo: 'editar', cliente: row });
            }}
          >
            Editar
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              alternarAtivo(row);
            }}
            disabled={atualizar.isPending}
          >
            {row.active ? 'Desativar' : 'Ativar'}
          </button>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              setErroExclusao(null);
              setParaExcluir(row);
            }}
          >
            Excluir
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Clientes"
        meta="Cadastro de clientes para reaproveitar dados nas propostas."
        actions={<Button onClick={() => setModal({ modo: 'novo' })}>Novo cliente</Button>}
      />

      <div className="toolbar">
        <Field
          label="Buscar"
          wrapperClassName="cli-search"
          placeholder="Razão social, CNPJ/CPF ou contato"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <Field as="select" label="UF" wrapperClassName="cli-filter-uf" value={uf} onChange={(e) => setUf(e.target.value)}>
          <option value="">Todos os estados</option>
          {(estadosQuery.data ?? []).map((estado) => (
            <option key={estado.uf} value={estado.uf}>
              {estado.uf} — {estado.nome}
            </option>
          ))}
        </Field>
        <Field
          as="select"
          label="Status"
          wrapperClassName="cli-filter-status"
          value={ativo}
          onChange={(e) => setAtivo(e.target.value as FiltroAtivo)}
        >
          <option value="">Todos</option>
          <option value="1">Ativos</option>
          <option value="0">Inativos</option>
        </Field>
        <div className="toolbar-spacer" />
      </div>

      <DataTable
        columns={colunas}
        rows={linhas}
        rowKey={(row) => row.id}
        loading={listaQuery.isLoading}
        onRowClick={(row) => navigate(`/clientes/${row.id}`)}
        empty={
          semFiltros ? (
            <EmptyState
              title="Nenhum cliente cadastrado ainda"
              description="Cadastre o primeiro para reaproveitar os dados nas propostas."
              action={<Button onClick={() => setModal({ modo: 'novo' })}>Novo cliente</Button>}
            />
          ) : (
            <EmptyState
              title="Nenhum cliente encontrado"
              description="Tente outro termo de busca ou outro filtro."
              action={
                <Button
                  variant="ghost"
                  onClick={() => {
                    setBusca('');
                    setUf('');
                    setAtivo('');
                  }}
                >
                  Limpar filtros
                </Button>
              }
            />
          )
        }
      />

      {total > 0 && totalPaginas > 1 ? (
        <div className="row row-between cli-paginacao">
          <Button variant="ghost" size="sm" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
            Anterior
          </Button>
          <span className="muted cli-paginacao__info">
            Página {pagina} de {totalPaginas} · {total} {total === 1 ? 'cliente' : 'clientes'}
          </span>
          <Button variant="ghost" size="sm" disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)}>
            Próxima
          </Button>
        </div>
      ) : null}

      {modal ? (
        <ModalCliente
          modo={modal.modo}
          cliente={modal.modo === 'editar' ? modal.cliente : undefined}
          onClose={() => setModal(null)}
        />
      ) : null}

      <ConfirmDialog
        open={paraExcluir !== null}
        title="Excluir cliente"
        tone="danger"
        confirmLabel="Excluir"
        loading={excluir.isPending}
        onCancel={() => {
          setParaExcluir(null);
          setErroExclusao(null);
        }}
        onConfirm={confirmarExclusao}
        message={
          <div className="stack-sm">
            <p>
              Excluir <strong>{paraExcluir?.razaoSocial}</strong>? Esta ação não pode ser desfeita.
            </p>
            {erroExclusao ? (
              <p className="form-alert form-alert-error" role="alert">
                {erroExclusao}
              </p>
            ) : null}
          </div>
        }
      />
    </>
  );
}

// -------------------------------------------------------- modal · cliente --

export function ModalCliente({
  modo,
  cliente,
  onClose,
}: {
  modo: 'novo' | 'editar';
  cliente?: ClienteResumo;
  onClose: () => void;
}) {
  const toast = useToast();
  const criar = useCriarCliente();
  const atualizar = useAtualizarCliente();
  const estadosQuery = useEstados();

  const [razaoSocial, setRazaoSocial] = useState(cliente?.razaoSocial ?? '');
  const [cnpj, setCnpj] = useState(cliente?.cnpj ?? '');
  const [contato, setContato] = useState(cliente?.contato ?? '');
  const [celular, setCelular] = useState(cliente?.celular ?? '');
  const [email, setEmail] = useState(cliente?.email ?? '');
  const [uf, setUf] = useState(cliente?.uf ?? '');
  const [notes, setNotes] = useState(cliente?.notes ?? '');
  const [active, setActive] = useState(cliente?.active ?? true);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});

  const salvando = criar.isPending || atualizar.isPending;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErroGeral(null);
    setErros({});

    if (!isValidDocumento(cnpj)) {
      setErros({ cnpj: 'CNPJ/CPF inválido.' });
      return;
    }

    const input: CreateClientInput = {
      razaoSocial,
      cnpj,
      contato: contato || undefined,
      celular: celular || undefined,
      email: email || undefined,
      uf: uf || undefined,
      notes: notes || undefined,
      active,
    };

    try {
      if (modo === 'novo') {
        const criado = await criar.mutateAsync(input);
        toast.success(`${criado.razaoSocial} cadastrado.`);
      } else if (cliente) {
        await atualizar.mutateAsync({ id: cliente.id, input });
        toast.success('Cliente atualizado.');
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setErroGeral(err.message);
        setErros(err.fields ?? {});
      } else {
        setErroGeral('Não foi possível salvar agora. Tente de novo.');
      }
    }
  }

  return (
    <Modal
      open
      title={modo === 'novo' ? 'Novo cliente' : 'Editar cliente'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button type="submit" form="form-cliente" disabled={salvando}>
            {salvando ? 'Salvando…' : modo === 'novo' ? 'Cadastrar cliente' : 'Salvar alterações'}
          </Button>
        </>
      }
    >
      <form id="form-cliente" className="stack" onSubmit={handleSubmit} noValidate>
        {erroGeral ? (
          <p className="form-alert form-alert-error" role="alert">
            {erroGeral}
          </p>
        ) : null}
        <div className="field-grid">
          <Field
            label="Razão social"
            required
            value={razaoSocial}
            onChange={(e) => setRazaoSocial(e.target.value)}
            error={erros.razaoSocial}
          />
          <Field
            label="CNPJ / CPF"
            required
            inputMode="numeric"
            placeholder="CNPJ ou CPF"
            value={cnpj}
            onChange={(e) => setCnpj(maskDocumento(e.target.value))}
            error={erros.cnpj}
          />
        </div>
        <div className="field-grid">
          <Field label="Contato" value={contato} onChange={(e) => setContato(e.target.value)} error={erros.contato} />
          <Field
            label="Celular"
            type="tel"
            value={celular}
            onChange={(e) => setCelular(maskPhone(e.target.value))}
            error={erros.celular}
          />
        </div>
        <div className="field-grid">
          <Field label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={erros.email} />
          <Field as="select" label="UF" value={uf} onChange={(e) => setUf(e.target.value)} error={erros.uf}>
            <option value="">Sem estado</option>
            {(estadosQuery.data ?? []).map((estado) => (
              <option key={estado.uf} value={estado.uf}>
                {estado.uf} — {estado.nome}
              </option>
            ))}
          </Field>
        </div>
        <Field as="textarea" label="Observações" value={notes} onChange={(e) => setNotes(e.target.value)} error={erros.notes} />
        <label className="checkbox">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Cliente ativo
        </label>
      </form>
    </Modal>
  );
}
